/**
 * Step 5 — Notifications [TYPE A — Recommended]
 *
 * Embeds NotificationsPage directly — same screen as Settings → Notifications.
 * Hides PageHeader and fixed footer via scoped DOM ref.
 * Save & Continue triggers the hidden Save button so all existing
 * notification save logic (validation, toast, rowVersion) runs as normal.
 *
 * Visibility gate: canManageNotifications() from usePermissions().
 * If false, skip this step entirely.
 */

import { useRef, useEffect, useCallback } from 'react';
import { NotificationsPage } from '@/components/Pages/NotificationsPage';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';

interface Step5NotificationsProps {
  onNext: (status: 'completed' | 'skipped') => void;
  onBack: () => void;
}

function useHideNotificationsChrome(embedRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const apply = () => {
      const container = embedRef.current;
      if (!container) return;

      // 1. Hide PageHeader (first child of space-y-6 root)
      const root = container.querySelector<HTMLElement>('div.space-y-6');
      if (root) {
        const firstChild = root.children[0] as HTMLElement | undefined;
        if (firstChild) firstChild.style.display = 'none';
        // Remove pb-24 reserved for the fixed footer
        root.style.paddingBottom = '0';
      }

      // 2. Hide fixed bottom footer — scoped entirely within embed container
      container.querySelectorAll<HTMLElement>('div.fixed').forEach((el) => {
        const style = window.getComputedStyle(el);
        if (style.bottom === '0px') {
          el.style.display = 'none';
          el.setAttribute('data-wizard-hidden', 'true');
        }
      });
    };

    apply();
    const t1 = setTimeout(apply, 100);
    const t2 = setTimeout(apply, 400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      embedRef.current
        ?.querySelectorAll<HTMLElement>('[data-wizard-hidden="true"]')
        .forEach((el) => {
          el.style.display = '';
          el.removeAttribute('data-wizard-hidden');
        });
    };
  }, [embedRef]);
}

export function Step5Notifications({ onNext, onBack }: Step5NotificationsProps) {
  const embedRef = useRef<HTMLDivElement>(null);
  useHideNotificationsChrome(embedRef);

  const triggerSave = useCallback(() => {
    const container = embedRef.current;
    if (!container) return;
    const saveBtn = container.querySelector<HTMLButtonElement>(
      '[data-wizard-hidden="true"] button:not([disabled]):last-of-type',
    );
    saveBtn?.click();
  }, []);

  const handleNext = useCallback(() => {
    triggerSave();
    onNext('completed');
  }, [triggerSave, onNext]);

  const handleSkip = useCallback(() => {
    onNext('skipped');
  }, [onNext]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Embedded notifications panel */}
      <div className="flex-1 min-h-0 overflow-hidden px-3 pt-3 pb-1">
        <div
          className="flex flex-col h-full min-h-0 border border-border rounded-lg bg-white overflow-hidden"
          style={{ transform: 'translateZ(0)', willChange: 'transform' }}
        >
          <div ref={embedRef} className="flex-1 min-h-0 overflow-y-auto">
            <NotificationsPage />
          </div>
        </div>
      </div>

      {/* Wizard bottom bar */}
      <div className="shrink-0 border-t bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground max-w-xl">
            Set up reminders for submission deadlines, approval chasers, and manager digests
            before your team submits their first timesheet.
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="gap-1 text-muted-foreground"
            >
              <SkipForward className="h-4 w-4" />
              Skip for now
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="gap-1 bg-[#00796B] hover:bg-[#00695C] text-white"
            >
              Save & Continue
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
